import React, { useCallback, useState } from "react";
import { api } from "../../lib/axios";
import { isE2EStagingMode } from "../../lib/e2eMode";

type SummaryResponse = {
  success?: boolean;
  businessEvents?: unknown[];
  notifications?: unknown[];
  operationalEvents?: unknown[];
  timelineMerged?: unknown[];
  wallet?: unknown;
  consistencyIssues?: unknown[];
  error?: string;
};

export default function AdminE2EDebugPage(): React.JSX.Element {
  const [clientId, setClientId] = useState("");
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const id = Number(clientId.trim());
    if (!Number.isFinite(id) || id <= 0) {
      setError("Saisissez un clientId numérique valide.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<SummaryResponse>(
        `/api/admin/e2e-debug/summary?clientId=${encodeURIComponent(String(id))}&take=120`
      );
      if (data.success === false) {
        setError(String(data.error ?? "Erreur API"));
        setSummary(null);
        return;
      }
      setSummary(data);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger le diagnostic (vérifiez NODE_ENV / E2E_DEBUG_TOOLS côté API).");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const download = async (type: "timeline" | "events" | "notifications" | "wallet") => {
    const id = Number(clientId.trim());
    if (!Number.isFinite(id) || id <= 0) {
      setError("clientId requis pour l’export.");
      return;
    }
    try {
      const { data } = await api.get(`/admin/e2e-debug/export?clientId=${id}&type=${type}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `e2e-export-${type}-client-${id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError("Export JSON impossible.");
    }
  };

  const e2eUi = isE2EStagingMode();

  return (
    <div className="space-y-6 text-slate-100">
      <div>
        <h1 className="text-xl font-bold text-white">Debug E2E</h1>
        <p className="mt-1 text-sm text-slate-400">
          Panneau réservé admin — données temps réel + exports JSON. API :{" "}
          <code className="text-slate-300">/api/admin/e2e-debug/*</code> (désactivée en production sauf{" "}
          <code className="text-slate-300">E2E_DEBUG_TOOLS=1</code>).
        </p>
        {e2eUi ? (
          <p className="mt-2 text-xs text-amber-300/90">
            Bandeau debug admin : actif uniquement si{" "}
            <code>VITE_E2E_MODE=true</code> (voir <code>.env.local</code>).
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-400">Client ID (Prisma)</span>
          <input
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="ex. 12"
          />
        </label>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          Charger
        </button>
      </div>

      {error ? <p className="text-sm text-rose-400">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
          onClick={() => void download("timeline")}
        >
          Export timeline
        </button>
        <button
          type="button"
          className="rounded border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
          onClick={() => void download("events")}
        >
          Export événements
        </button>
        <button
          type="button"
          className="rounded border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
          onClick={() => void download("notifications")}
        >
          Export notifications
        </button>
        <button
          type="button"
          className="rounded border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
          onClick={() => void download("wallet")}
        >
          Export wallet
        </button>
      </div>

      {summary ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <h2 className="mb-2 text-sm font-semibold text-white">Business events (mémoire)</h2>
            <pre className="max-h-80 overflow-auto text-[11px] text-slate-300">
              {JSON.stringify(summary.businessEvents ?? [], null, 2)}
            </pre>
          </section>
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <h2 className="mb-2 text-sm font-semibold text-white">Notifications client</h2>
            <pre className="max-h-80 overflow-auto text-[11px] text-slate-300">
              {JSON.stringify(summary.notifications ?? [], null, 2)}
            </pre>
          </section>
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <h2 className="mb-2 text-sm font-semibold text-white">operational_events</h2>
            <pre className="max-h-80 overflow-auto text-[11px] text-slate-300">
              {JSON.stringify(summary.operationalEvents ?? [], null, 2)}
            </pre>
          </section>
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <h2 className="mb-2 text-sm font-semibold text-white">Timeline fusionnée</h2>
            <pre className="max-h-80 overflow-auto text-[11px] text-slate-300">
              {JSON.stringify(summary.timelineMerged ?? [], null, 2)}
            </pre>
          </section>
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 lg:col-span-2">
            <h2 className="mb-2 text-sm font-semibold text-white">Wallet + ledger</h2>
            <pre className="max-h-80 overflow-auto text-[11px] text-slate-300">
              {JSON.stringify(summary.wallet ?? null, null, 2)}
            </pre>
          </section>
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 lg:col-span-2">
            <h2 className="mb-2 text-sm font-semibold text-white">Cohérence métier (échantillon)</h2>
            <pre className="max-h-80 overflow-auto text-[11px] text-slate-300">
              {JSON.stringify(summary.consistencyIssues ?? [], null, 2)}
            </pre>
          </section>
        </div>
      ) : null}
    </div>
  );
}
